import { getLang, tr } from '@app/i18n'
import type { Venue } from '@app/rvf'
import { localized, resolveProfiles, resolveTargetRef } from '@app/rvf'
import { useEffect, useState } from 'preact/hooks'
import { selectTarget } from './actions'
import { IsoLazy } from './iso/iso-lazy'
import { importChunk } from './lazy-retry'
import { extraBasemapFor } from './map/extra-basemaps'
import type { MapTap } from './map/map-taps'
import type { VenueMap as VenueMapFacade } from './map/venue-map'
import { useMapInsets } from './map-insets'
import { holdCamera } from './nav/camera-hold'
import { activePromos, startPromoClock, stopPromoClock } from './promo'
import {
  a2bOpen,
  activeLevelId,
  alarmYouLive,
  anchorId,
  appState,
  arNotice,
  arSession,
  basemapMode,
  canEditVenue,
  emergencyFrame,
  floorStackUnitId,
  focusUnitId,
  hazardPickMode,
  hazardPickPoint,
  isOffline,
  langTick,
  measureActive,
  measurePoints,
  parkedCar,
  pendingTargetId,
  routePlan,
  savedMapAvailable,
  signRoute,
  styleResetTick,
  swReady,
  targetId,
  travelMode,
  updateReady,
  venue,
  venueMap,
  view3D,
  viewMode,
} from './state'
import { isPointMode, resolveMapTap, type TapModes } from './tap-mode'
import { A2bPlanner } from './ui/a2b-planner'
import { AlarmBar } from './ui/alarm-bar'
import { ArLazy } from './ui/ar-lazy'
import { ArrivedPanel } from './ui/arrived-panel'
import { BasemapFallbackToast } from './ui/basemap-fallback-toast'
import { CarBar } from './ui/car-bar'
import { CatalogPanel } from './ui/catalog-panel'
import { Chips } from './ui/chips'
import { EmergencyLazy } from './ui/emergency-lazy'
import { FloorStackPanel } from './ui/floor-stack-panel'
import { FollowLazy } from './ui/follow-lazy'
import { Header } from './ui/header'
import { Legend } from './ui/legend'
import { MallExplorer } from './ui/mall-explorer'
import { MeasureBar } from './ui/measure-bar'
import { PacksLazy } from './ui/packs-lazy'
import { PromoBlock } from './ui/promo-block'
import { QrPassModal } from './ui/qr-pass'
import { QrScanLazy } from './ui/qr-scan-lazy'
import { RoutePanel } from './ui/route-panel'
import { SearchPanel } from './ui/search-panel'
import { ShoppingFab, ShoppingPanel } from './ui/shopping-panel'
import { SignRouteScheme } from './ui/sign-route-fallback'
import { SosBar } from './ui/sos-bar'
import { StaffHazardLazy } from './ui/staff-hazard-lazy'
import { StartArrow } from './ui/start-arrow'
import { TargetCard } from './ui/target-card'
import { EmptyHint, ToolRail } from './ui/terra-widgets'
import { Volume3dOverlay } from './ui/volume3d-overlay'

function tapModes(): TapModes {
  return { hazardPick: hazardPickMode.value, measure: measureActive.value }
}

/**
 * Любой тап по карте или маркеру (map/map-taps.ts): решение — одно, в
 * tap-mode.ts. Режимы «укажите точку» забирают тап целиком — ни помещение,
 * ни маркер POI в них не становятся целью маршрута (фидбек владельца 25.09).
 */
function onMapTap(tap: MapTap): void {
  const action = resolveMapTap(tap, tapModes())
  switch (action.kind) {
    case 'hazard-point':
      // Точку и форму дальше ведёт staff-hazard-sheet.tsx (staff/staff-api.ts).
      hazardPickMode.value = false
      hazardPickPoint.value = action.lngLat
      return
    case 'measure-point': {
      const next = [...measurePoints.value, action.lngLat] as const
      measurePoints.value = next
      venueMap.value?.setMeasure(next)
      return
    }
    case 'select': {
      const state = appState.value
      if (state === 'overview' || state === 'target') selectTarget(action.id)
      return
    }
    case 'none':
      return
  }
}

function isMapPointMode(): boolean {
  return isPointMode(tapModes())
}

/**
 * Человек двигает карту руками: камера отдаёт ему управление (camera-hold.ts).
 * Отдаём только во время ведения — в обзоре камера и так никуда не едет, и
 * лишний «удержан» зря показал бы кнопку возврата.
 */
function onUserGesture(): void {
  if (appState.value === 'follow') holdCamera()
}

/** Смена подложки пересобрала стиль — вернуть состояние venue-слоёв. */
function onStyleReset(): void {
  const map = venueMap.value
  if (!map) return
  map.setLevel(activeLevelId.value)
  map.set3D(view3D.value)
  map.setRouteProfile(travelMode.value)
  if (routePlan.value) map.setRoute(routePlan.value)
  map.setDimmed(appState.value === 'route')
  map.highlight(targetId.value)
  map.setTargetFocus(focusUnitId.value)
  if (measureActive.value) map.setMeasure(measurePoints.value)
  // Аварийный режим: фокус и сигнальный маршрут перевешиваются в панели.
  styleResetTick.value++
}

/** Позиция «Вы здесь» из QR-якоря (?a=) — честный якорь-маркер (без пульса). */
function showAnchorPosition(map: VenueMapFacade): void {
  const v = venue.value
  const id = anchorId.value
  if (!v || !id) return
  const anchor = v.anchors.find((a) => a.id === id)
  if (anchor) map.setYou(anchor.point, null, tr('map.you_are_here'), 'anchor')
}

function useVenueMap(): void {
  const v = venue.value
  useEffect(() => {
    if (!v || venueMap.value) return
    const container = document.getElementById('map')
    if (!container) return
    // Фасад карты грузится вместе с самой картой (ADR 0002): его модульный
    // граф — слои, маркеры, маршрут — весит десятки килобайт и в стартовом
    // бандле не нужен никому, кроме экрана с картой.
    importChunk(() => import('./map/venue-map'))
      .then(({ VenueMap }) =>
        VenueMap.create(
          container,
          v,
          { onTap: onMapTap, isPointMode: isMapPointMode, onStyleReset, onUserGesture },
          basemapMode.value,
        ),
      )
      .then((map: VenueMapFacade) => {
        map.setSignRoute(signRoute.value)
        venueMap.value = map
        savedMapAvailable.value = map.hasOfflineSnapshot()
        // Ортофото/Карта Эстонии/Схема, выбранные раньше, — поверх стартовой.
        const extra = extraBasemapFor(v.venue.center as [number, number], navigator.onLine)
        if (extra) map.setBasemapMode(extra)
        // Значок перехода на маршруте переключает этаж (route-levels.ts).
        map.onLevelSwitch = (levelId: string) => {
          activeLevelId.value = levelId
          map.setLevel(levelId)
        }
        // «Объём · вся площадка» с 29.07 — отдельная страница /mall/{venue},
        // грузить её сцену в простое незачем: у неё свой бандл.
        if (view3D.value) map.set3D(true) // сохранённый 3D-режим — сразу
        map.setRouteProfile(travelMode.value)
        // При тревоге маркером владеет живой GPS (emergency/live-you.ts).
        if (!alarmYouLive.peek()) showAnchorPosition(map)
        restoreTarget(map, v)
      })
      .catch((error: unknown) => {
        console.error('map init failed', error)
        appState.value = 'error'
      })
  }, [v])
}

/** Цель могла быть выбрана (чипом или ?to=) до готовности карты. */
function restoreTarget(map: VenueMapFacade, v: Venue): void {
  const to = pendingTargetId.value
  if (to) {
    pendingTargetId.value = null
    // `?to=` принимает и id, и код места («R155») — продавец печатает QR
    // с кодом полки, не зная внутренних id карты (products.ts).
    const resolved = resolveTargetRef(v, to) ?? to
    // Прямая ссылка (арендатор «Посмотреть на карте») — фокус на здании.
    focusUnitId.value = unitOfTarget(v, resolved)
    selectTarget(resolved)
  } else if (targetId.value) {
    map.highlight(targetId.value)
    map.setTargetFocus(focusUnitId.value)
  }
}

/**
 * Оплаченное выделение на карте (ADR 0019). Что показывать — решает promo.ts;
 * здесь только передача в слой. Часы промо запускаются раз: кампания обязана
 * гаснуть в свою полночь, а не при следующей перезагрузке вкладки.
 */
function usePromoLayer(): void {
  const promos = activePromos.value
  const map = venueMap.value
  useEffect(() => {
    startPromoClock()
    return stopPromoClock
  }, [])
  useEffect(() => {
    if (!map) return
    const featured: string[] = []
    const promo: string[] = []
    const badges: { unitId: string; badge: string }[] = []
    for (const [unitId, item] of promos) {
      ;(item.tier === 'featured' ? featured : promo).push(unitId)
      if (item.badge) badges.push({ unitId, badge: item.badge })
    }
    map.setPromo(featured, promo)
    map.setPromoBadges(badges)
  }, [map, promos])
}

/**
 * Подпись якоря переводится при смене языка (маркер живёт вне Preact-дерева).
 * Пока тревога ведёт маркер по живому GPS (alarmYouLive), якорь не рисуем —
 * он перекрыл бы точку человека; отбой возвращает якорь на место таблички.
 */
function useAnchorLabelRefresh(): void {
  const tick = langTick.value
  const live = alarmYouLive.value
  useEffect(() => {
    const map = venueMap.value
    if (map && appState.value !== 'follow' && !live) showAnchorPosition(map)
  }, [tick, live])
}

/** Тост «Вы у входа N · карта уже знает, где вы» — 3.5 с при входе по якорю (B1). */
function AnchorToast() {
  const [visible, setVisible] = useState(true)
  const v = venue.value
  const id = anchorId.value
  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 3500)
    return () => clearTimeout(timer)
  }, [])
  if (!visible || !v || !id) return null
  const anchor = v.anchors.find((a) => a.id === id)
  if (!anchor) return null
  const name = localized(anchor.name, getLang(), v.venue.default_language)
  return (
    <div class="anchor-toast" role="status">
      <span class="anchor-toast-qr" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          aria-hidden="true"
          focusable="false"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v1" />
        </svg>
      </span>
      <span>
        <strong>{name || tr('map.you_are_here')}</strong> · {tr('anchor.toast_hint')}
      </span>
    </div>
  )
}

/** Оверлеи состояния ОБЗОР: бейдж, тост якоря, машина, чипы, подсказка. */
function OverviewLayer() {
  return (
    <>
      {(swReady.value || isOffline.value) && (
        <p class="offline-badge">{tr(isOffline.value ? 'offline.cached' : 'offline.ready')}</p>
      )}
      {anchorId.value ? <AnchorToast /> : parkedCar.value === null ? <EmptyHint /> : null}
      {venue.value && <PacksLazy venue={venue.value} />}
      <QrScanLazy onVenue={(path) => location.assign(path)} />
      <CarBar />
      <Chips />
    </>
  )
}

/** Инструменты карты (ОБЗОР/ЦЕЛЬ): этажи/ТЦ, подложка, замер, A-to-B, легенда. */
function MapStateTools({ mallPreset }: { mallPreset: boolean }) {
  return (
    <>
      {mallPreset && <MallExplorer />}
      <ToolRail floors={!mallPreset && (venue.value?.levels.length ?? 0) > 1} />
      <CatalogPanel />
      <Legend />
      {measureActive.value && <MeasureBar />}
      {a2bOpen.value && <A2bPlanner />}
      <StaffHazardLazy />
    </>
  )
}

/** Уведомление AR-слоя (спека 13): переживает закрытие оверлея, гаснет сам. */
function ArNoticeToast() {
  const key = arNotice.value
  useEffect(() => {
    if (!key) return
    const timer = setTimeout(() => {
      arNotice.value = null
    }, 2600)
    return () => clearTimeout(timer)
  }, [key])
  if (!key) return null
  return (
    <p class="ar-toast" role="status">
      {tr(key)}
    </p>
  )
}

/** Промт «доступна новая версия»: кнопка перезагрузки, поверх любого состояния. */
function UpdateToast() {
  if (!updateReady.value) return null
  return (
    <button type="button" class="update-toast" onClick={() => location.reload()}>
      {tr('update.ready')} · <strong>{tr('update.reload')}</strong>
    </button>
  )
}

/** Здание, к которому относится цель: сам юнит или дом арендатора-точки. */
function unitOfTarget(v: Venue, targetId: string): string | null {
  if (v.units.some((u) => u.id === targetId)) return targetId
  const poi = v.pois.find((p) => p.id === targetId)
  if (!poi) return null
  const inRing = (pt: readonly number[], ring: readonly number[][]): boolean => {
    let hit = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i]?.[0] as number
      const yi = ring[i]?.[1] as number
      const xj = ring[j]?.[0] as number
      const yj = ring[j]?.[1] as number
      const y = pt[1] as number
      if (yi > y !== yj > y && (pt[0] as number) < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        hit = !hit
      }
    }
    return hit
  }
  const host = v.units.find((u) => {
    const ring = u.geometry.coordinates[0]
    return u.level === poi.level && ring !== undefined && inRing(poi.point, ring)
  })
  return host?.id ?? null
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: корневая сборка экрана — линейный список условных оверлеев, дробление скроет порядок наложения
export function App() {
  langTick.value
  const state = appState.value
  const inEmergency = emergencyFrame.value !== null
  // Режим ТЦ (референс MallExplorer): iso-capability + мультиэтаж.
  const v = venue.value
  const mallPreset = v !== null && resolveProfiles(v.venue).capabilities.iso && v.levels.length > 1
  useVenueMap()
  // Центр карты — в видимое окно (под камерой AR, над панелью ведения).
  useMapInsets()
  usePromoLayer()
  useAnchorLabelRefresh()
  // Ссылка «Редактировать» — только владельцу/админу. Спрашиваем публичный
  // эндпоинт прав: раньше дёргали settings кабинета, и анонимный посетитель
  // получал 401 — браузер писал ошибку в консоль на каждой загрузке карты.
  useEffect(() => {
    const id = venue.value?.venue.id
    if (!id) return
    canEditVenue.value = false
    fetch(`/api/venues/${encodeURIComponent(id)}/can-edit`, { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<{ can_edit?: boolean }>) : null))
      .then((data) => {
        canEditVenue.value = data?.can_edit === true
      })
      .catch(() => {
        canEditVenue.value = false
      })
  }, [venue.value])

  // Aurora: светлая «Paper» — по умолчанию (без класса), тёмные подложки →
  // body.theme-dark («Dusk», отдельная композиция токенов, не инверсия).
  // theme-light — переходный синоним для немигрированных Terra-стилей (D17).
  useEffect(() => {
    const dark = basemapMode.value === 'dark' || basemapMode.value === 'fiord'
    document.body.classList.toggle('theme-dark', dark)
    document.body.classList.toggle('theme-light', !dark)
  }, [basemapMode.value])

  return (
    <div class="viewer">
      <div id="map" class="map" />
      {viewMode.value === 'iso' && state !== 'loading' && state !== 'error' && <IsoLazy />}
      {floorStackUnitId.value !== null && <FloorStackPanel />}
      <Volume3dOverlay />
      {state === 'loading' && (
        <div class="overlay">
          <p>{tr('map.loading')}</p>
        </div>
      )}
      {state === 'error' && (
        <div class="overlay">
          {/* Путь с таблички (&r=, уровень 0): рисуем и без venue.json/движка. */}
          {signRoute.value && <SignRouteScheme points={signRoute.value} />}
          <p class="overlay-error">{tr('map.error')}</p>
        </div>
      )}
      {/* Аварийный режим (G4): поверх карты, поверх всех остальных состояний */}
      {inEmergency && <EmergencyLazy />}
      <AlarmBar />
      <SosBar venueId={v?.venue.id ?? null} />
      {!inEmergency && (state === 'overview' || state === 'target') && (
        <>
          <Header />
          <SearchPanel />
          <ShoppingFab />
          <ShoppingPanel />
        </>
      )}
      {!inEmergency && (state === 'overview' || state === 'target') && viewMode.value === 'map' && (
        <MapStateTools mallPreset={mallPreset} />
      )}
      {!inEmergency && state === 'overview' && <OverviewLayer />}
      {!inEmergency && state === 'overview' && viewMode.value === 'map' && <PromoBlock />}
      {!inEmergency && state === 'target' && floorStackUnitId.value === null && <TargetCard />}
      {!inEmergency && state === 'route' && <RoutePanel />}
      {!inEmergency && state === 'orient' && <StartArrow />}
      {!inEmergency && state === 'follow' && <FollowLazy />}
      {!inEmergency && state === 'arrived' && <ArrivedPanel />}
      {/* AR-ведение (спека 13): оверлей камеры поверх карты, ниже листов/ЧС */}
      {arSession.value !== null && <ArLazy />}
      <ArNoticeToast />
      <BasemapFallbackToast />
      <QrPassModal />
      <UpdateToast />
    </div>
  )
}
